import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { DollarSign, Building, Shield } from 'lucide-react';

interface FinancialAssessmentProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_2_FinancialAssessment: React.FC<FinancialAssessmentProps> = ({
  data,
  onUpdate,
  tier,
}) => {
  const updateFinancialField = (field: string, value: string | number | boolean | string[]) => {
    onUpdate({
      ...data,
      financialPosition: {
        ...data.financialPosition,
        [field]: value,
      },
    });
  };

  const updateAssetsField = (field: string, value: string | string[]) => {
    onUpdate({
      ...data,
      assets: {
        ...data.assets,
        [field]: value,
      },
    });
  };

  const handleBankingRelationshipChange = (bank: string, checked: boolean) => {
    const currentBanks = data.financialPosition?.bankingRelationships || [];
    const updatedBanks = checked ? [...currentBanks, bank] : currentBanks.filter((b) => b !== bank);
    updateFinancialField('bankingRelationships', updatedBanks);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Financial Position & Assets</h3>
        <p className="text-gray-600">
          Understanding your community's financial health helps us calculate accurate ROI
          projections
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Treasury & Finances */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Financial Overview
            </CardTitle>
            <CardDescription>Current financial position and cash flow</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="treasuryAmount">Current Treasury/Reserve Amount *</Label>
              <Input
                id="treasuryAmount"
                type="number"
                value={data.financialPosition?.treasuryAmount || ''}
                onChange={(e) =>
                  updateFinancialField('treasuryAmount', parseFloat(e.target.value) || 0)
                }
                placeholder="e.g., 150000"
              />
              <p className="text-xs text-gray-500 mt-1">
                This helps us calculate investment potential and ROI projections
              </p>
            </div>

            <div>
              <Label htmlFor="monthlyHOAFees">Monthly HOA Fee (per unit)</Label>
              <Input
                id="monthlyHOAFees"
                type="number"
                value={data.financialPosition?.monthlyHOAFees || ''}
                onChange={(e) =>
                  updateFinancialField('monthlyHOAFees', parseFloat(e.target.value) || 0)
                }
                placeholder="e.g., 250"
              />
            </div>

            <div>
              <Label htmlFor="annualBudget">Annual Budget</Label>
              <Input
                id="annualBudget"
                type="number"
                value={data.financialPosition?.annualBudget || ''}
                onChange={(e) =>
                  updateFinancialField('annualBudget', parseFloat(e.target.value) || 0)
                }
                placeholder="e.g., 500000"
              />
            </div>

            <div>
              <Label htmlFor="reserveFunds">Emergency/Capital Reserve Funds</Label>
              <Input
                id="reserveFunds"
                type="number"
                value={data.financialPosition?.reserveFunds || ''}
                onChange={(e) =>
                  updateFinancialField('reserveFunds', parseFloat(e.target.value) || 0)
                }
                placeholder="e.g., 100000"
              />
            </div>

            <div>
              <Label htmlFor="outstandingDebts">Outstanding Debts/Loans</Label>
              <Input
                id="outstandingDebts"
                type="number"
                value={data.financialPosition?.outstandingDebts || ''}
                onChange={(e) =>
                  updateFinancialField('outstandingDebts', parseFloat(e.target.value) || 0)
                }
                placeholder="e.g., 50000"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="investmentAccounts"
                checked={data.financialPosition?.investmentAccounts || false}
                onCheckedChange={(checked) => updateFinancialField('investmentAccounts', checked)}
              />
              <Label htmlFor="investmentAccounts">
                Currently have investment accounts (CDs, money market, etc.)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Community Assets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-600" />
              Community Assets
            </CardTitle>
            <CardDescription>
              Physical assets and infrastructure owned by the community
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="buildings">Buildings Owned by Community</Label>
              <Textarea
                id="buildings"
                value={data.assets?.buildings?.join(', ') || ''}
                onChange={(e) =>
                  updateAssetsField('buildings', e.target.value.split(', ').filter(Boolean))
                }
                placeholder="e.g., Clubhouse, Pool House, Maintenance Shed"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="infrastructure">Infrastructure & Utilities</Label>
              <Textarea
                id="infrastructure"
                value={data.assets?.infrastructure?.join(', ') || ''}
                onChange={(e) =>
                  updateAssetsField('infrastructure', e.target.value.split(', ').filter(Boolean))
                }
                placeholder="e.g., Private roads, Water system, Electrical grid"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="landOwnership">Land Ownership Structure</Label>
              <Select
                value={data.assets?.landOwnership || ''}
                onValueChange={(value) => updateAssetsField('landOwnership', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select ownership type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual_lots">Individual lot ownership</SelectItem>
                  <SelectItem value="common_areas">Common areas only</SelectItem>
                  <SelectItem value="mixed">Mixed ownership structure</SelectItem>
                  <SelectItem value="cooperative">Cooperative ownership</SelectItem>
                  <SelectItem value="other">Other structure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="sharedFacilities">Shared Facilities</Label>
              <Textarea
                id="sharedFacilities"
                value={data.assets?.sharedFacilities?.join(', ') || ''}
                onChange={(e) =>
                  updateAssetsField('sharedFacilities', e.target.value.split(', ').filter(Boolean))
                }
                placeholder="e.g., Pool, Gym, Tennis Courts, Playground"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="equipmentOwned">Equipment & Vehicles</Label>
              <Textarea
                id="equipmentOwned"
                value={data.assets?.equipmentOwned?.join(', ') || ''}
                onChange={(e) =>
                  updateAssetsField('equipmentOwned', e.target.value.split(', ').filter(Boolean))
                }
                placeholder="e.g., Maintenance truck, Pool equipment, Landscaping tools"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banking Relationships */}
      {tier === 'gold' || tier === 'custom' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Banking Relationships
            </CardTitle>
            <CardDescription>
              Current banking relationships. Any financial integration is scoped and quoted
              separately, not included in a package.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Bank of America',
                'Wells Fargo',
                'Chase',
                'US Bank',
                'PNC Bank',
                'Local Credit Union',
                'Community Bank',
                'Other',
              ].map((bank) => (
                <div key={bank} className="flex items-center space-x-2">
                  <Checkbox
                    id={`bank-${bank}`}
                    checked={data.financialPosition?.bankingRelationships?.includes(bank) || false}
                    onCheckedChange={(checked) =>
                      handleBankingRelationshipChange(bank, checked as boolean)
                    }
                  />
                  <Label htmlFor={`bank-${bank}`}>{bank}</Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* ROI Preview */}
      {data.financialPosition?.treasuryAmount && data.financialPosition.treasuryAmount > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h4 className="font-semibold text-green-800 mb-2">Potential Annual Impact Preview</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency(data.financialPosition.treasuryAmount * 0.05)}
                  </div>
                  <div className="text-sm text-green-600">Conservative Investment Returns</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency((data.financialPosition.monthlyHOAFees || 0) * 12 * 0.15)}
                  </div>
                  <div className="text-sm text-green-600">Estimated Cost Savings</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-700">
                    {formatCurrency(
                      data.financialPosition.treasuryAmount * 0.05 +
                        (data.financialPosition.monthlyHOAFees || 0) * 12 * 0.15,
                    )}
                  </div>
                  <div className="text-sm text-green-600">Total Potential Annual Benefit</div>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                *Conservative estimates based on 5% investment returns and 15% operational savings
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Why this matters:</strong> Your financial position and assets help us:
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Size the financial transparency tools to your treasury</li>
          <li>• Scope any financial integrations as a separate quote</li>
          <li>• Structure implementation timeline based on your budget capacity</li>
        </ul>
      </div>
    </div>
  );
};
